import { useRoute, useRouter, type LocationQuery } from 'vue-router'
import { withQuery } from 'ufo'
import { omitPersonalParams } from 'refresh-common'

/**
 * stod.djurensratt.se's monthly-giver page for a donor leaving a one-time form's monthly tab: the
 * inbound string params, the chosen `sum` and `step=sum`. Always the standard page, never the
 * campaign's slug: most campaign slugs have no monthly-giver entry and 404 there. The inbound
 * `?campaignCode` stays behind, so the monthly form credits its own recruitment code: on a one-time
 * form it is a gift-structure id, which CreateMember2 rejects as a Varvningskod. Personal params a
 * campaign link brought (`PERSONAL_RESULT_PARAMS`: name, email, personnummer, address…) stay behind
 * too — they don't belong in a URL, and the monthly form asks for them itself.
 */
export function monthlyGiverUrl(routeQuery: LocationQuery, sum: number | undefined): string {
  const query = new URLSearchParams(
    Object.entries(omitPersonalParams(routeQuery))
      .filter(([_, v]) => typeof v === 'string')
      .map(([k, v]) => [k, v as string]),
  )
  if (sum) query.set('sum', String(sum))
  query.delete('campaignCode')
  query.set('step', 'sum')

  return withQuery('https://stod.djurensratt.se/manadsgivare', Object.fromEntries(query.entries()))
}

export function useTabbedDonationNavigation({
  blocks,
  initStep,
  sum,
  org,
}: {
  blocks: Ref<Record<string, any>>
  initStep: Ref<number>
  sum: Ref<number>
  org: string
}) {
  const route = useRoute()
  const router = useRouter()

  const handleTabNext = (type: 'single' | 'monthly', monthlyPath: string) => {
    if (type === 'monthly' && org === '1882') {
      // A campaign link's personal params stay behind: they don't belong in a URL (#2158).
      const query = new URLSearchParams(
        Object.entries(omitPersonalParams(route.query))
          .filter(([_, v]) => typeof v === 'string')
          .map(([k, v]) => [k, v as string]),
      )
      if (sum.value) query.set('sum', String(sum.value))

      return router.push({
        path: monthlyPath,
        query: Object.fromEntries(query.entries()),
      })
    } else if (type === 'monthly' && org === 'djurensratt') {
      // TODO: Remove special handling when monthly is live on new site
      return navigateTo(monthlyGiverUrl(route.query, sum.value), {
        external: true,
        open: { target: '_blank' },
      })
    }

    const stepKeys = Object.keys(blocks.value)
    const currentIndex = stepKeys.indexOf('sum')
    const next = stepKeys[currentIndex + 1]
    if (next) initStep.value = currentIndex + 1
  }

  return { handleTabNext }
}
